<template>
  <div class="review-wrap">
    <!-- 主体：左侧待审核列表 + 右侧当前包裹详情 -->
    <div class="review-main">
      <!-- 左侧：待审核列表（数据源为外部当前分页表格数据，顺序一致） -->
      <div class="review-left">
        <div class="review-left__head">
          <span>{{ $t('home.pendingReview') }} ({{ pendingList.length }})</span>
        </div>
        <div class="review-left__list">
          <div
            v-for="(item, index) in pendingList"
            :key="item.id"
            :class="['review-item', isActive(item) ? 'review-item--active' : '']"
            @click="selectRow(item, index)"
          >
            <div class="review-item__thumb">
              <img v-if="getItemThumb(item)" :src="getItemThumb(item)">
              <i v-else class="el-icon-picture-outline review-item__thumb-icon" />
            </div>
            <div class="review-item__info">
              <div class="review-item__no">{{ item.waybillNumber || '--' }}</div>
              <div class="review-item__sub">{{ item.preDeliverSiteIdName || '--' }}</div>
              <div class="review-item__sub">{{ item.uploadTime || '--' }}</div>
            </div>
            <el-tag :type="statusTagType(item.forceType)" size="mini">
              {{ statusText(item.forceType) }}
            </el-tag>
          </div>
          <div v-if="!pendingList.length" class="review-left__empty">--</div>
        </div>
      </div>

      <!-- 右侧：当前包裹详情 -->
      <div class="review-right">
        <div class="review-right__head">
          <span class="review-right__head-title">
            {{ $t('home.reviewPackage') }} {{ displayPosition }} / {{ pendingList.length }}
          </span>
          <div>
            <!-- 上一个 / 下一个：手动切换，切换后丢弃未提交的选择 -->
            <el-button :disabled="prevDisabled" size="small" @click="goPrev">
              {{ $t('home.prevOne') }}
            </el-button>
            <el-button :disabled="nextDisabled" size="small" @click="goNext">
              {{ $t('home.nextOne') }}
            </el-button>
          </div>
        </div>

        <div class="review-right__body">
          <!-- 包裹信息 -->
          <div class="review-card">
            <div class="review-card__title">
              <span>{{ $t('newOrder.waybillNumber') }}: {{ currentRow.waybillNumber || '--' }}</span>
              <i
                v-if="currentRow.waybillNumber"
                class="el-icon-document-copy review-card__copy"
                @click="copyWaybillNumber"
              />
            </div>
            <div class="review-card__cols">
              <div class="review-card__col">
                <div class="info-item">
                  <span class="info-label">{{ $t('GlobalSetObj.preEntrega') }}:</span>
                  <span class="info-value">{{ currentRow.preDeliverSiteIdName || '--' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('orderCenterCont.deliveryPerson') }}:</span>
                  <span class="info-value">{{ currentRow.delivererName || '--' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('collectionCenter.ReceivingAddress') }}:</span>
                  <span class="info-value">{{ currentRow.receiveAddr || '--' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('orderCenterCont.HouseNumber') }}:</span>
                  <span class="info-value">{{ currentRow.receiveHouseNumber || '--' }}</span>
                </div>
              </div>
              <div class="review-card__col">
                <!-- 暂未提供的数据统一展示 -- -->
                <div class="info-item">
                  <span class="info-label">{{ $t('home.recipient') }}:</span>
                  <span class="info-value">--</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('home.contactPhone') }}:</span>
                  <span class="info-value">--</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('home.customer') }}:</span>
                  <span class="info-value">--</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('home.merchant') }}:</span>
                  <span class="info-value">--</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 签收拍照 / 签收签名 -->
          <div class="review-card review-card--photos">
            <div class="review-photo-block review-photo-block--photo">
              <div class="review-photo-title">
                {{ $t('home.signedPhoto') }} ({{ photoList.length }})
              </div>
              <div class="review-photo-list">
                <el-image
                  v-for="(url, i) in photoList"
                  :key="'photo' + i"
                  :preview-src-list="[url]"
                  :src="getImgUrl(url, 800, 800)"
                  class="review-photo"
                  fit="cover"
                >
                  <div slot="error" class="image-slot">
                    <i class="el-icon-picture-outline" />
                  </div>
                </el-image>
                <span v-if="!photoList.length">--</span>
              </div>
            </div>
            <div class="review-photo-block review-photo-block--sign">
              <div class="review-photo-title">
                {{ $t('home.signedNamePhoto') }} ({{ signList.length }})
              </div>
              <div class="review-photo-list">
                <el-image
                  v-for="(url, i) in signList"
                  :key="'sign' + i"
                  :preview-src-list="[url]"
                  :src="getImgUrl(url, 800, 800)"
                  class="review-photo"
                  fit="cover"
                >
                  <div slot="error" class="image-slot">
                    <i class="el-icon-picture-outline" />
                  </div>
                </el-image>
                <span v-if="!signList.length">--</span>
              </div>
            </div>
          </div>

          <!-- 审核结果 -->
          <div v-if="showReviewArea" class="review-card">
            <div class="result-row">
              <span class="result-label">{{ $t('home.appliveResult') }}:</span>
              <div class="result-btns">
                <!-- 通过：直接提交并进入下一个（待审核 / 审核未通过 / 审核失败 均可点通过） -->
                <div class="result-btn result-btn--pass" @click="choosePass">
                  <i class="el-icon-check" />
                  <span>{{ $t('home.pass') }}</span>
                </div>
                <!-- 不通过：仅待审核状态展示，点击展开不通过原因 -->
                <div
                  v-if="canReview"
                  :class="[
                    'result-btn',
                    'result-btn--fail',
                    formInline.auditsState === 0 ? 'is-active' : ''
                  ]"
                  @click="chooseFail"
                >
                  <i class="el-icon-close" />
                  <span>{{ $t('home.noPass') }}</span>
                </div>
              </div>
            </div>
            <!-- 不通过原因：默认隐藏，选择「不通过」后展示且必选（多选）；两个都选自动提交 -->
            <div v-if="canReview && formInline.auditsState === 0" class="reason-row">
              <span class="result-label">{{ $t('home.failReason') }}:</span>
              <div class="reason-list">
                <div
                  :class="['reason-item', formInline.photoFail ? 'is-active' : '']"
                  @click="toggleReason('photo')"
                >
                  <i class="el-icon-picture-outline reason-item__icon" />
                  <div class="reason-item__text">
                    <div class="reason-item__name">{{ $t('home.photoStandard') }}</div>
                    <div class="reason-item__desc">{{ $t('home.photoFailDesc') }}</div>
                  </div>
                </div>
                <div
                  :class="['reason-item', formInline.signFail ? 'is-active' : '']"
                  @click="toggleReason('sign')"
                >
                  <i class="el-icon-edit-outline reason-item__icon" />
                  <div class="reason-item__text">
                    <div class="reason-item__name">{{ $t('home.signStandard') }}</div>
                    <div class="reason-item__desc">{{ $t('home.signFailDesc') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 非待审核（已审核）状态 -->
          <div v-else class="review-card review-card--reviewed">
            <span class="result-label">{{ $t('home.appliveResult') }}:</span>
            <el-tag size="small" type="success">{{ $t('home.reviewedTag') }}</el-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="review-footer">
      <el-button size="small" @click="closeDrawer">{{ $t('GlobalSetObj.Close') }}</el-button>
      <div class="review-footer__right">
        <el-checkbox v-model="autoNext">{{ $t('home.autoEnterNext') }}</el-checkbox>
        <!-- 仅待审核状态需要手动确认（通过、两个原因都选时均为自动提交） -->
        <el-button
          v-if="canReview"
          :disabled="formInline.auditsState === ''"
          size="small"
          type="primary"
          @click="confirmAndNext"
        >
          {{ $t('home.confirmAndNext') }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { lgStorageForceSignAudits } from '@/api/orderApi';
import { copyData } from '@/utils/copyData';

export default {
  name: 'SignatureReviewWorkbench',
  props: {
    // 外部当前分页的表格数据（待审核列表数据源，顺序与外部列表一致）
    list: {
      type: Array,
      default: () => []
    },
    // 点击审核进入时选中的包裹 id
    currentId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      // 待审核列表（内部副本，审核完成的会从列表移除）
      pendingList: [],
      // 右侧当前展示的包裹
      currentRow: {},
      // 当前包裹在 pendingList 中的下标；-1 表示当前包裹已审核并从列表移除
      currentIndex: -1,
      // 最近一次移除的下标，用于移除后「上一个 / 下一个」的定位
      lastRemovedIndex: 0,
      // 审核后自动进入下一个（默认勾选）
      autoNext: true,
      // 提交中，防止重复提交
      submitting: false,
      formInline: {
        // 审核结果：1 通过，0 不通过，'' 未选择
        auditsState: '',
        // 照片不合格
        photoFail: false,
        // 签名不合格
        signFail: false
      }
    };
  },
  computed: {
    photoList() {
      return (this.currentRow && this.currentRow.signedPhoto) || [];
    },
    signList() {
      return (this.currentRow && this.currentRow.signedNamePhoto) || [];
    },
    // 待审核：可通过 / 不通过
    canReview() {
      return !!this.currentRow && this.currentRow.forceType === 1;
    },
    // 审核未通过(3) / 审核失败(4)：仅可再次「通过」
    canPassOnly() {
      const forceType = this.currentRow && this.currentRow.forceType;
      return forceType === 3 || forceType === 4;
    },
    // 是否展示审核操作区
    showReviewArea() {
      return this.canReview || this.canPassOnly;
    },
    // 「上一个 / 下一个」的定位锚点
    anchorIndex() {
      return this.currentIndex >= 0 ? this.currentIndex : this.lastRemovedIndex;
    },
    prevDisabled() {
      return this.anchorIndex <= 0;
    },
    nextDisabled() {
      return this.anchorIndex >= this.pendingList.length - 1;
    },
    // 头部展示的序号
    displayPosition() {
      if (!this.pendingList.length) {
        return 0;
      }
      return Math.min(this.anchorIndex, this.pendingList.length - 1) + 1;
    }
  },
  mounted() {
    // 取外部表格数据的副本作为待审核列表，顺序与外部列表一致；
    // 与操作列「审核」按钮的出现条件保持一致：非已通过（forceType !== 2）且非已签收（waybillStatus !== 10）才进入列表
    this.pendingList = this.list.filter((item) => item.forceType !== 2 && item.waybillStatus !== 10);
    const idx = this.pendingList.findIndex((item) => item.id === this.currentId);
    const index = idx > -1 ? idx : 0;
    if (this.pendingList.length) {
      this.currentRow = this.pendingList[index];
      this.currentIndex = index;
      this.lastRemovedIndex = index;
    }
  },
  methods: {
    // 是否为当前展示的包裹
    isActive(item) {
      return this.currentRow && this.currentRow.id === item.id;
    },
    // 列表缩略图（优先取第一张签收拍照，其次签名）
    getItemThumb(item) {
      const first =
        (item.signedPhoto && item.signedPhoto[0]) ||
        (item.signedNamePhoto && item.signedNamePhoto[0]);
      return this.getImgUrl(first, 200, 200);
    },
    // 腾讯云图片处理参数：按需缩放/压缩，避免加载原图
    getImgUrl(url, w, h) {
      if (!url) {
        return '';
      }
      return `${url}?imageMogr2/thumbnail/${w}x${h}>/quality/80/format/jpg/ignore-error/1`;
    },
    // 审核状态文案
    statusText(forceType) {
      const map = {
        1: this.$t('home.pendingReview'),
        2: this.$t('customerManagements.approved'),
        3: this.$t('home.unapplivefail'),
        4: this.$t('home.applivefail')
      };
      return map[forceType] || this.$t('home.pendingReview');
    },
    // 审核状态标签颜色
    statusTagType(forceType) {
      const map = { 1: 'warning', 2: 'success', 3: 'danger', 4: 'danger' };
      return map[forceType] || 'info';
    },
    // 复制运单号
    copyWaybillNumber() {
      if (this.currentRow.waybillNumber) {
        copyData(this.currentRow.waybillNumber);
      }
    },
    // 重置审核表单（切换包裹时丢弃未提交的选择）
    resetForm() {
      this.formInline = {
        auditsState: '',
        photoFail: false,
        signFail: false
      };
    },
    // 选中左侧列表中的包裹
    selectRow(item, index) {
      if (this.isActive(item)) {
        return;
      }
      this.currentRow = item;
      this.currentIndex = index;
      this.lastRemovedIndex = index;
      // 切换包裹，丢弃未提交的选择
      this.resetForm();
    },
    // 上一个
    goPrev() {
      const target = Math.max(0, this.anchorIndex - 1);
      this.selectRow(this.pendingList[target], target);
    },
    // 下一个
    goNext() {
      const target = this.currentIndex >= 0 ? this.currentIndex + 1 : this.lastRemovedIndex;
      const index = Math.min(this.pendingList.length - 1, Math.max(0, target));
      this.selectRow(this.pendingList[index], index);
    },
    // 点击「通过」：直接提交并进入下一个（无需二次确认）
    choosePass() {
      if (!this.showReviewArea || this.submitting) {
        return;
      }
      this.formInline.auditsState = 1;
      this.formInline.photoFail = false;
      this.formInline.signFail = false;
      this.submitReview();
    },
    // 点击「不通过」：展开不通过原因；再次点击取消选择
    chooseFail() {
      if (!this.canReview || this.submitting) {
        return;
      }
      if (this.formInline.auditsState === 0) {
        this.resetForm();
        return;
      }
      this.formInline.auditsState = 0;
      this.formInline.photoFail = false;
      this.formInline.signFail = false;
    },
    // 选择不通过原因：两个同时选中时自动提交
    toggleReason(type) {
      if (this.formInline.auditsState !== 0 || this.submitting) {
        return;
      }
      if (type === 'photo') {
        this.formInline.photoFail = !this.formInline.photoFail;
      } else {
        this.formInline.signFail = !this.formInline.signFail;
      }
      // 照片不合格 + 签名不合格 同时选中：默认自动确认并进入下一个
      if (this.formInline.photoFail && this.formInline.signFail) {
        this.submitReview();
      }
    },
    // 底部「确定并进入下一个」：只选一个不通过原因时手动提交
    confirmAndNext() {
      if (!this.canReview || this.submitting) {
        return;
      }
      if (this.formInline.auditsState === '') {
        this.$message.error(this.$t('changepwd.selectResult'));
        return;
      }
      this.submitReview();
    },
    // 提交审核
    submitReview() {
      // 不通过必须选择原因
      if (
        this.formInline.auditsState === 0 &&
        !this.formInline.photoFail &&
        !this.formInline.signFail
      ) {
        this.$message.error(this.$t('home.tip1002'));
        return;
      }
      const isPass = this.formInline.auditsState === 1;
      const data = {
        id: this.currentRow.id,
        auditsState: this.formInline.auditsState,
        // 选中的原因传 0，未选中传 1（沿用原接口口径）
        photoStandard: !isPass && this.formInline.photoFail ? 0 : 1,
        signStandard: !isPass && this.formInline.signFail ? 0 : 1
      };
      this.submitting = true;
      lgStorageForceSignAudits(data)
        .then((res) => {
          if (res.code === 200) {
            this.$message.success(this.$t('GlobalSetObj.TheOperationSucceeded'));
            this.handleReviewed(data.auditsState);
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    // 审核成功后的处理：从待审核列表移除，并按需进入下一个
    handleReviewed(auditsState) {
      const idx = this.currentIndex >= 0 ? this.currentIndex : this.lastRemovedIndex;
      // 本地同步审核状态（通过 -> 2 已通过；不通过 -> 3 审核未通过）
      this.$set(this.currentRow, 'forceType', auditsState === 1 ? 2 : 3);
      // 已发送审核的，直接从待审核列表移除
      this.pendingList.splice(idx, 1);
      // 全部审核完成：关闭抽屉并通知父级刷新分页
      if (!this.pendingList.length) {
        this.$emit('call-parent-method', false);
        return;
      }
      if (this.autoNext) {
        // 自动进入下一个
        const next = Math.min(idx, this.pendingList.length - 1);
        this.currentRow = this.pendingList[next];
        this.currentIndex = next;
        this.lastRemovedIndex = next;
        this.resetForm();
      } else {
        // 取消勾选：停在当前（已审核）的包裹，等待手动进入下一个
        this.currentIndex = -1;
        this.lastRemovedIndex = Math.min(idx, this.pendingList.length);
        this.resetForm();
      }
    },
    // 关闭抽屉（父级会重新查询分页）
    closeDrawer() {
      this.$emit('call-parent-method', false);
    }
  }
};
</script>

<style scoped>
.review-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #f5f6f7;
}

.review-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ===== 左侧待审核列表 ===== */
.review-left {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebeef5;
  background: #fff;
}

.review-left__head {
  padding: 14px 16px;
  font-size: 16px;
  font-weight: bold;
  border-bottom: 1px solid #ebeef5;
}

.review-left__list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.review-left__empty {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.review-item {
  display: flex;
  align-items: center;
  padding: 10px;
  border-left: 3px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.review-item + .review-item {
  margin-top: 6px;
}

.review-item:hover {
  background: #f5f7fa;
}

.review-item--active {
  background: #eaf7ef;
  border-left-color: #00a551;
}

.review-item__thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 10px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f2f2f2;
}

.review-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.review-item__thumb-icon {
  font-size: 20px;
  color: #c0c4cc;
}

.review-item__info {
  flex: 1;
  min-width: 0;
}

.review-item__no {
  font-size: 13px;
  font-weight: bold;
  color: #303133;
  word-break: break-all;
}

.review-item__sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

/* ===== 右侧详情 ===== */
.review-right {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.review-right__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;
}

.review-right__head-title {
  font-size: 16px;
  font-weight: bold;
}

.review-right__body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.review-card {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}

.review-card__title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
}

.review-card__copy {
  margin-left: 8px;
  color: #909399;
  cursor: pointer;
}

.review-card__cols {
  display: flex;
}

.review-card__col {
  flex: 1;
  min-width: 0;
}

.info-item {
  display: flex;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 20px;
}

.info-label {
  margin-right: 8px;
  color: #909399;
  flex-shrink: 0;
}

.info-value {
  color: #303133;
  word-break: break-all;
}

/* ===== 照片 ===== */
.review-card--photos {
  display: flex;
}

.review-photo-block--photo {
  flex: 2;
  min-width: 0;
}

.review-photo-block--sign {
  flex: 1;
  min-width: 0;
}

.review-photo-title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: bold;
}

.review-photo-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.review-photo {
  width: 120px;
  height: 120px;
  border-radius: 4px;
}

/* ===== 审核结果 ===== */
.result-row {
  display: flex;
  align-items: center;
}

.result-label {
  margin-right: 16px;
  font-size: 15px;
  flex-shrink: 0;
}

.result-btns {
  display: flex;
  gap: 16px;
}

.result-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 44px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.result-btn i {
  margin-right: 6px;
}

.result-btn--pass {
  border-color: #00a551;
  background: #00a551;
  color: #fff;
}

.result-btn--fail.is-active {
  border-color: #f56c6c;
  color: #f56c6c;
}

.reason-row {
  display: flex;
  margin-top: 20px;
}

.reason-list {
  display: flex;
  flex: 1;
  gap: 16px;
}

.reason-item {
  display: flex;
  align-items: center;
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.reason-item.is-active {
  border-color: #00a551;
  color: #00a551;
}

.reason-item__icon {
  margin-right: 12px;
  font-size: 24px;
}

.reason-item__name {
  font-size: 15px;
}

.reason-item__desc {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

.review-card--reviewed {
  display: flex;
  align-items: center;
}

/* ===== 底部操作栏 ===== */
.review-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

.review-footer__right {
  display: flex;
  align-items: center;
  gap: 16px;
}
</style>
